"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
// import { toast } from "@/hooks/use-toast"
import { AppContext } from "@/App"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import { Input } from "@/Components/ui/input"
import React, { useState } from "react"
import { Eye, EyeClosed, KeyRound, Mail } from "lucide-react"
import { useIsMobile } from "@/hooks/use-mobile"

export type UserLogin = {
    email: string
    password: string
    newPassword: string
}

const formSchema = z.object({
    email: z.string(),
    currentPassword: z.string(),
    newPassword: z.string(),
    verifyPassword: z.string()
})

const UserLogin = () => {
    const appContext = React.useContext(AppContext);
    const isMobile = useIsMobile();
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const handleShowPasswordClick = () => {
        setShowPassword(!showPassword); // toggle show password
    }

    React.useEffect(() => {
        form.setFocus("currentPassword");
    }, []);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: appContext.applicationUser?.email,
            currentPassword: "",
            newPassword: "",
            verifyPassword: ""
        },
    });

    function checkPasswordRequirements(password: string) {
        // check if the password meets the minimum requirements
        if (password.length < 6) {
            appContext.showActionNotification("Error", "Password must be at least 6 characters long!", "destructive");
            return false;
        }
        if (!/[a-z]/.test(password)) {
            appContext.showActionNotification("Error", "Password must contain at least one lowercase letter!", "destructive");
            return false;
        }
        if (!/[A-Z]/.test(password)) {
            appContext.showActionNotification("Error", "Password must contain at least one uppercase letter!", "destructive");
            return false;
        }
        if (!/[0-9]/.test(password)) {
            appContext.showActionNotification("Error", "Password must contain at least one number!", "destructive");
            return false;
        }
        if (!/[!@#$%^&*]/.test(password)) {
            appContext.showActionNotification("Error", "Password must contain at least one special character!", "destructive");
            return false;
        }
        return true;
    }

    function onSubmit(data: z.infer<typeof formSchema>) {
        // check if the new password and verify password match
        if (data.newPassword !== data.verifyPassword) {
            appContext.showActionNotification("Error", "New Password and Verify Password do not match!", "destructive");
            return;
        }

        // check if the new password is the same as the current password
        if (data.newPassword === data.currentPassword) {
            appContext.showActionNotification("Error", "New Password cannot be the same as the Current Password!", "destructive");
            return;
        }

        // check if the password meets the minimum requirements
        if (!checkPasswordRequirements(data.newPassword)) {
            return;
        }
        // send the data to the server
        fetch('/Account/change-password', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response;
            })
            .then(() => {
                appContext.showActionNotification("Success", "Password for " + appContext.applicationUser?.email + " Updated Successfully!", "success");
            })
            .catch(error => {
                console.log(`Error: ${error}`);
                appContext.showActionNotification("Error", `Error: ${error}`, "destructive");
            });

        form.reset();
        form.setFocus("currentPassword");
    }

    return (
        <div className={`${isMobile ? "w-full p-2 text-2xl" : "w-1/3 p-2"} mx-auto`}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className={`${isMobile ? "text-2xl" : ""}`}>Email</FormLabel>
                                <FormControl>
                                    {/* <Input placeholder="Email" {...field} disabled={true} /> */}
                                    <div className="md:flex md:items-center mb-6">
                                        <div className="flex flex-row w-full bg-gray-200 py-1 rounded">
                                            <label className="text-gray-500 font-bold content-center ml-1" htmlFor="email"><Mail className={`${isMobile ? "h-7 w-7" : ""}`} /></label>
                                            <Input
                                                placeholder="john.doe@example.com"
                                                {...field} 
                                                disabled={true}
                                                className={`bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none ${isMobile ? "text-2xl" : ""}`}
                                            />
                                        </div>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="currentPassword"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className={`${isMobile ? "text-2xl" : ""}`}>Current Password</FormLabel>
                                <FormControl>
                                    {/* <Input type="password" {...field} /> */}
                                    <div className="flex flex-row w-full bg-gray-200 rounded">
                                        <label className="text-gray-500 font-bold content-center ml-1" htmlFor="currentPassword"><KeyRound className={`${isMobile ? "h-7 w-7" : ""}`} /></label>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="******"
                                            {...field}
                                            className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                        />
                                        <div className="content-center bg-gray-200 text-gray-500 p-2 rounded" onClick={handleShowPasswordClick}>{showPassword ? <Eye className={`${isMobile ? "h-7 w-7" : ""}`} /> : <EyeClosed className={`${isMobile ? "h-7 w-7" : ""}`} />}</div>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="newPassword"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className={`${isMobile ? "text-2xl" : ""}`}>New Password</FormLabel>
                                <FormControl>
                                    {/* <Input type="password" {...field} /> */}
                                    <div className="flex flex-row w-full bg-gray-200 rounded">
                                        <label className="text-gray-500 font-bold content-center ml-1" htmlFor="newPassword"><KeyRound className={`${isMobile ? "h-7 w-7" : ""}`} /></label>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="******"
                                            {...field}
                                            className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                        />
                                        <div className="content-center bg-gray-200 text-gray-500 p-2 rounded" onClick={handleShowPasswordClick}>{showPassword ? <Eye className={`${isMobile ? "h-7 w-7" : ""}`} /> : <EyeClosed className={`${isMobile ? "h-7 w-7" : ""}`} />}</div>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="verifyPassword"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className={`${isMobile ? "text-2xl" : ""}`}>Verify New Password</FormLabel>
                                <FormControl>
                                    <div className="flex flex-row w-full bg-gray-200 rounded">
                                        <label className="text-gray-500 font-bold content-center ml-1" htmlFor="verifyPassword"><KeyRound className={`${isMobile ? "h-7 w-7" : ""}`} /></label>
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="******"
                                            {...field}
                                            className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-full py-2 px-4 text-gray-700 leading-tight focus:outline-none"
                                        />
                                        <div className="content-center bg-gray-200 text-gray-500 p-2 rounded" onClick={handleShowPasswordClick}>{showPassword ? <Eye className={`${isMobile ? "h-7 w-7" : ""}`} /> : <EyeClosed className={`${isMobile ? "h-7 w-7" : ""}`} />}</div>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className="h-12"></div>
                    <button type="submit"  className={`button-default ${isMobile ? "text-2xl w-full h-12 mt-16" : "justify-end"}`}>Update Password</button>
                </form>
            </Form>
        </div>
    )
}

export default UserLogin